import { ReactNode, Component, CSSProperties } from 'react';
import _ from 'lodash';
import './Heatmap.css';
import { opacity } from "@Utils/Colors";
import { IComponentSizeProp } from './Interfaces';
import { Infotip, InfotipProps } from './Infotip';
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
// ------------------------------------------------------------------------------------------------
export interface HeatmapProps extends IComponentSizeProp {
  /**
   * Labels along axis-X.
   * E.g.: ['1am', '2am', '3am']
   */
  xLabels: (number | string)[];

  /**
   * Labels along axis-Y.
   * E.g.: ['Sun', 'Mon']
   */
  yLabels: (number | string)[];

  /**
   * 2D Array of numbers having yLabels.length rows and xLabels.length rows	
   * E.g.: [[2,3,5][5,6,9]]
   */
  data: number[][];

  /**
   * The base color for the Heatmap	
   * Default: '#329fff'
   */
  background?: string;

  /**
   * Height of each cell of the Heatmap in px
   * Default: 25
   */
  minCellHeight?: number;

  /**
   * Height of each cell of the Heatmap in px
   * Default: 35
   */
  minCellWidth?: number;

  /**
   * Width of the y label area in pixel
   * Default: 40
   */
  yLabelWidth?: number;

  /**
   * Location of y labels. It can be top or bottom
   * Default: 'top'
   */
  xLabelsLocation?: 'top' | 'bottom';

  /**
   * 	Array of bool conveying which x labels to display.
   * For ex: [true, false, true, false] means the 1st and the 3rd labels will be displayed and the 2nd and 4th will be hidden
   */
  xLabelsVisibility?: boolean[];

  /**
   * Text alignment of the yLabels
   * Default: 'right'
   */
  yLabelTextAlign?: 'right' | 'left' | 'center';

  /**
   * If set to true, axis-Y will be shown
   * Default: true
   */
  displayYLabels?: boolean;

  /**
   * Unit to display next to the value on hover
   */
  unit?: string;

  /**
   * Adds an handler to cell click.
   * Default: undefined
   * 
   * @param {string} yi - Index in axis-Y.
   * @param {string} xi - Index in axis-X.
   */
  onClick?: (yi: number, xi: number) => void;

  /**
   * If set to true will render cells as square
   * Default: false
   */
  squares?: boolean;

  /**
   * Render custom content in cell
   * @param {string} value - Cell's value.
   * @param {string} x - Cell's x label.
   * @param {string} y - Cell's y label.
   */
  cellRender?: (value: number, x: number | string, y: number | string) => ReactNode;

  /**
   * function To set custom cell style.It is useful for using own colour scheme
   * @param {string} background - Background color.
   * @param {string} value - Cell's value.
   * @param {string} min - Minimum value.
   * @param {string} max - Maximum value.
   * @param {string} data - Dataset.
   * @param {string} xi - Index in axis-X.
   * @param {string} yi - Index in axis-Y.
   */
  cellStyle?: (background: string, value: number, min: number, max: number, data: number[][], xi: number, yi: number) => CSSProperties;

  /**
   * Optional selected yi,xi
   */
  selectedCell?: [number, number];

  /**
   * Key-value pairs for legend
   */
  legendPairs?: {
    label: string;
    color: string;
    invisible?: boolean;
  }[];

  /**
   * Width of the legend label area in pixel
   * Default: 60
   */
  legendLabelWidth?: number;
}
// ------------------------------------------------------------------------------------------------
interface HeatmapState {
  tooltipProps: InfotipProps;
}
// ------------------------------------------------------------------------------------------------
// todo: use grid diplay (https://css-tricks.com/snippets/css/complete-guide-grid/)
export class Heatmap extends Component<HeatmapProps, HeatmapState>{
  // ------------------------------------------------------------------------------------------------
  public static defaultProps: HeatmapProps = {
    width: 200,
    height: 200,
    data: [],
    xLabels: [],
    yLabels: [],
    background: '#329fff',
    minCellHeight: 60,
    minCellWidth: 40,
    yLabelWidth: 60,
    yLabelTextAlign: 'right',
    unit: '',
    xLabelsLocation: 'top',
    xLabelsVisibility: null,
    displayYLabels: true,
    onClick: undefined,
    squares: false,
    cellRender: null,
    cellStyle: (background, value, min, max) => {
      return {
        background: background,
        opacity: opacity(value, min, max),
      };
    },
    legendPairs: [],
    legendLabelWidth: 60
  };
  // ------------------------------------------------------------------------------------------------
  constructor(props: HeatmapProps) {
    super(props);

    this.state = {
      tooltipProps: {
        open: false,
        content: '',
        xPos: 0,
        yPos: 0
      },
    };
  }
  // ------------------------------------------------------------------------------------------------
  render() {
    const {
      xLabels, yLabels, data,
      background,
      width, height,
      minCellHeight, minCellWidth,
      yLabelWidth, yLabelTextAlign,
      xLabelsLocation, xLabelsVisibility,
      unit,
      displayYLabels,
      onClick,
      squares,
      cellRender,
      cellStyle,
      selectedCell,
      legendPairs,
      legendLabelWidth
    } = this.props;

    // calculate width/height
    let cellWidth: number;
    let cellHeight: number;
    let legendCellHeight: number;
    let legendWidth = isDefined(legendPairs) && legendPairs.length > 0 ? legendLabelWidth : 0;
    let gridWidth = width - (displayYLabels ? yLabelWidth : 0) - legendWidth;

    if (xLabels.length > 0) {
      cellWidth = Math.floor(gridWidth / xLabels.length);
      if (cellWidth < minCellWidth) {
        cellWidth = minCellWidth;
      }
    }
    else
      cellWidth = minCellWidth;

    if (yLabels.length > 0) {
      const rows = _.max([
        // 1 row is used for X labels
        (yLabels.length + 1),
        // legend rows
        isDefined(legendPairs) ? legendPairs.length : 0
      ]);
      cellHeight = Math.floor(height / rows);

      if (cellHeight < minCellHeight) {
        cellHeight = minCellHeight;
      }
    }
    else
      cellHeight = minCellHeight;

    // if squares; choose minimum value
    if (squares) {
      cellWidth = _.min([cellWidth, cellHeight]);
      cellHeight = cellWidth;
    }

    if (legendWidth > 0) {
      // (legend cells are always square)
      legendCellHeight = _.min([cellWidth, cellHeight]);
    }

    let cursor = '';
    if (onClick !== undefined) {
      cursor = 'pointer';
    }
    const paddingTop = xLabelsLocation === 'top' ? cellHeight : 0;
    const xLabelsElement = <div
      style={{
        display: 'inline-flex',
        whiteSpace: 'nowrap',
        height: cellHeight
      }}
    >
      <XLabels
        labels={xLabels}
        labelsVisibility={xLabelsVisibility}
        cellHeight={cellHeight}
        cellWidth={cellWidth}
      />
    </div>;

    const flatArray = data.reduce((i, o) => [...o, ...i], []);
    const max = Math.max(...flatArray);
    const min = Math.min(...flatArray);

    const yLabelsJSX: ReactNode[] = [];
    let legendLabelsJSX: ReactNode[] = [];
    const gridJSX: ReactNode[] = [];
    const customCellRender = isDefined(cellRender);
    const useCustomCellStyle = isDefined(cellStyle);
    _.forEach(yLabels, (y, yi) => {

      if (displayYLabels)
        yLabelsJSX.push(< div
          style={{
            textAlign: yLabelTextAlign,
            paddingRight: '5px',
            width: yLabelWidth,
            height: cellHeight,
            lineHeight: cellHeight + "px"
          }}
        >
          {/* todo: add label auto size*/}
          {y}
        </div>);

      gridJSX.push(<div
        key={yi}
        style={{
          whiteSpace: 'nowrap',
          height: cellHeight
        }}>
        {_.map(xLabels, (x, xi) => {
          const value = data[yi][xi];
          const title = (value || value === 0) && `${value} ${unit}`;
          const extraStyles = useCustomCellStyle ?
            cellStyle(background, value, min, max, data, xi, yi)
            : {};

          const selected = isDefined(selectedCell)
            && selectedCell[0] == yi
            && selectedCell[1] == xi;

          return <div
            onClick={() => {
              if (isDefined(onClick))
                onClick(yi, xi);
            }}
            key={`${yi}_${xi}`}
            className={selected ? 'cell-selected' : undefined}
            style={{
              display: 'inline-block',
              cursor: `${cursor}`,
              height: cellHeight,
              width: cellWidth,
              lineHeight: cellHeight + "px",
              textAlign: 'center',
              ...extraStyles
            }}
            onMouseEnter={e =>
              !IsNullOrEmpty(title) &&
              this._showTooltip(
                title,
                e.pageX,
                e.pageY)}
            onMouseLeave={() => !IsNullOrEmpty(title) &&
              this._hideTooltip()
            }
          >
            {customCellRender ? cellRender(value, x, y) : value}
          </div>;
        })
        }
      </div>);
    });

    let legendPaddingLeft = 0;
    if (legendWidth > 0) {
      legendPaddingLeft = 10;
      legendLabelsJSX = _.map(legendPairs, (pair, index) => {
        return <div
          key={index}
          style={{
            display: 'flex',
            whiteSpace: 'nowrap',
            width: legendWidth,
            height: legendCellHeight,
            lineHeight: legendCellHeight + "px"
          }}>

          <div
            key={0}
            style={{
              width: legendCellHeight,
              height: legendCellHeight,
              background: pair.color
            }}
          >
          </div>

          <div
            key={1}
            style={{
              height: legendCellHeight,
              width: legendWidth - legendCellHeight - legendPaddingLeft,
              textAlign: 'left',
              paddingLeft: '5px',
              visibility: pair.invisible === true ? 'hidden' : 'visible',
            }}>
            {/* todo: add label auto size*/}
            {pair.label}
          </div>
        </div>;
      });
    }

    return (
      <div
        className='heatmap-primitive'
        style={{ display: 'flex' }}>

        <Infotip
          key="cell-tooltip"
          maxWidth={350}
          {...this.state.tooltipProps}
        />

        {/* Y Labels */}
        {
          displayYLabels ?
            <div
              key='y-labels'
              style={{
                width: yLabelWidth,
                height: '100%',
                paddingTop: paddingTop
              }}>
              {yLabelsJSX}
            </div>
            : null
        }

        {/* Grid & X Labels */}
        <div style={{
          width: gridWidth,
          height: '100%',
          overflowX: 'auto',
          overflowY: 'hidden'
        }}>
          {xLabelsLocation === 'top' && xLabelsElement}
          {gridJSX}
          {xLabelsLocation === 'bottom' && xLabelsElement}
        </div>

        {/* Legend */}
        <div style={{
          width: legendWidth,
          height: '100%',
          paddingLeft: legendPaddingLeft,
          paddingTop: paddingTop
        }}>
          {legendLabelsJSX}
        </div>
      </div>
    );
  }
  // ------------------------------------------------------------------------------------------------
  private _showTooltip(msg: string, x: number, y: number) {
    this.setState({ tooltipProps: { open: true, content: msg, xPos: x, yPos: y } });
  }
  // ------------------------------------------------------------------------------------------------
  private _hideTooltip() {
    this.setState({ tooltipProps: { open: false, content: '', xPos: 0, yPos: 0 } });
  }
  // ------------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------
interface XLabelsProps {
  labels: (number | string)[];
  labelsVisibility: boolean[];
  cellWidth: number;
  cellHeight: number;
}
// ------------------------------------------------------------------------------------------------
class XLabels extends Component<XLabelsProps, {}> {
  // ------------------------------------------------------------------------------------------------
  public static defaultProps: XLabelsProps = {
    labels: [],
    labelsVisibility: null,
    cellHeight: 30,
    cellWidth: 30,
  };
  // ------------------------------------------------------------------------------------------------
  constructor(props: XLabelsProps) {
    super(props);
  }
  // ------------------------------------------------------------------------------------------------
  render() {
    const { labels, cellWidth, labelsVisibility, cellHeight } = this.props;
    return _.map(labels, (x, i) => (
      <div
        key={i}
        style={{
          display: 'inline-block',
          textAlign: 'center',
          width: cellWidth,
          height: cellHeight,
          visibility:
            labelsVisibility && !labelsVisibility[i] ? 'hidden' : 'visible',
        }}
      >
        {x}
      </div>
    ));
  }
  // ------------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------

